/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
} from '@angular/core';

/**
 * DebugOverlayComponent
 * ---------------------
 * Overlay flotante de diagnóstico para device real. Renderiza en tiempo real:
 *  - Coordenadas del último touch (start/move/end/cancel)
 *  - Elemento target bajo el dedo (tag + class + id)
 *  - touch-action computado del target y de su cadena hasta ion-content
 *  - scrollTop / scrollHeight / clientHeight del ion-content activo
 *  - Contadores por tipo de evento touch / pointer / wheel
 *  - Marca si el evento llegó con defaultPrevented === true
 *  - Detector de element bloqueante: si touchend NO llega después de touchstart en N ms
 *
 * Cómo activar:
 *   - Edita src/environments/environment.ts y agrega `debugOverlay: true`
 *   - O en consola del Web Inspector ejecuta: `localStorage.setItem('debugOverlay','1')`
 *     y recarga la app. Funciona también sin rebuild si el componente ya está montado.
 *
 * El componente se auto-desactiva si window.location.search incluye `?nodebug=1`.
 */
@Component({
  selector: 'app-debug-overlay',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      *ngIf="enabled"
      class="dbg-overlay"
      [style.left.px]="position.x"
      [style.top.px]="position.y"
      (touchstart)="onDragStart($event)"
      (touchmove)="onDrag($event)"
      (touchend)="onDragEnd()"
    >
      <div class="dbg-header">
        <strong>🔬 Debug touch</strong>
        <button (click)="toggleMinimize()" class="dbg-btn">
          {{ minimized ? '▢' : '_' }}
        </button>
        <button (click)="enabled = false" class="dbg-btn">✕</button>
      </div>

      <div *ngIf="!minimized" class="dbg-body">
        <div class="dbg-row"><span>Last:</span><b>{{ lastEvent }}</b></div>
        <div class="dbg-row"><span>Coords:</span><b>{{ coords }}</b></div>
        <div class="dbg-row">
          <span>Target:</span><b>{{ targetTag }}</b>
        </div>
        <div class="dbg-row"><span>#id/.class:</span><b>{{ targetIdClass }}</b></div>
        <div class="dbg-row">
          <span>touch-action:</span>
          <b [class.dbg-bad]="touchActionBad">{{ touchAction }}</b>
        </div>
        <div class="dbg-row">
          <span>pointer-events:</span>
          <b [class.dbg-bad]="pointerEventsBad">{{ pointerEvents }}</b>
        </div>
        <div class="dbg-row"><span>z-index:</span><b>{{ zIndex }}</b></div>
        <div class="dbg-row"><span>position:</span><b>{{ position_css }}</b></div>
        <div class="dbg-row">
          <span>defaultPrevented:</span>
          <b [class.dbg-bad]="lastDefaultPrevented">{{ lastDefaultPrevented }}</b>
        </div>

        <div class="dbg-section">Counters</div>
        <div class="dbg-grid">
          <div>start: <b>{{ counters.touchstart }}</b></div>
          <div>move: <b>{{ counters.touchmove }}</b></div>
          <div>end: <b>{{ counters.touchend }}</b></div>
          <div>cancel: <b>{{ counters.touchcancel }}</b></div>
        </div>
        <div class="dbg-row" *ngIf="missingEnds > 0">
          <span class="dbg-warn">⚠ touchstart sin touchend:</span>
          <b>{{ missingEnds }}</b>
        </div>

        <div class="dbg-section">ion-content</div>
        <div class="dbg-row"><span>scrollTop:</span><b>{{ scrollTop }}</b></div>
        <div class="dbg-row"><span>scrollHeight:</span><b>{{ scrollHeight }}</b></div>
        <div class="dbg-row"><span>clientHeight:</span><b>{{ clientHeight }}</b></div>

        <div class="dbg-section">Stacking chain</div>
        <div class="dbg-chain">
          <div *ngFor="let item of stackingChain" class="dbg-chain-item">
            <span class="dbg-chain-tag">{{ item.tag }}</span>
            <span>pe:{{ item.pe }}</span>
            <span>ta:{{ item.ta }}</span>
            <span>z:{{ item.z }}</span>
            <span>pos:{{ item.pos }}</span>
          </div>
        </div>

        <div class="dbg-row dbg-actions">
          <button class="dbg-btn dbg-btn-action" (click)="dumpToConsole()">
            Dump console
          </button>
          <button class="dbg-btn dbg-btn-action" (click)="highlightTarget()">
            Highlight target
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .dbg-overlay {
        position: fixed;
        z-index: 2147483647; /* sobre cualquier cosa */
        width: 280px;
        font: 11px/1.3 -apple-system, system-ui, sans-serif;
        color: #fff;
        background: rgba(0, 0, 0, 0.82);
        border: 1px solid #ff00aa;
        border-radius: 8px;
        padding: 4px 6px;
        pointer-events: auto;
        user-select: none;
        backdrop-filter: blur(4px);
        touch-action: none;
      }
      .dbg-header {
        display: flex;
        align-items: center;
        gap: 4px;
        padding-bottom: 4px;
        border-bottom: 1px solid #444;
      }
      .dbg-header strong {
        flex: 1;
        font-size: 11px;
      }
      .dbg-btn {
        background: #222;
        color: #fff;
        border: 1px solid #555;
        border-radius: 4px;
        padding: 1px 6px;
        font-size: 10px;
      }
      .dbg-body {
        padding-top: 4px;
        max-height: 50vh;
        overflow-y: auto;
      }
      .dbg-row {
        display: flex;
        justify-content: space-between;
        gap: 6px;
        padding: 1px 0;
      }
      .dbg-row span {
        color: #aaa;
      }
      .dbg-row b {
        color: #0f0;
        word-break: break-all;
        text-align: right;
        max-width: 60%;
      }
      .dbg-row b.dbg-bad {
        color: #ff5577;
      }
      .dbg-row .dbg-warn {
        color: #ffaa00;
      }
      .dbg-section {
        margin: 6px 0 2px;
        padding-top: 4px;
        border-top: 1px dashed #444;
        color: #ff00aa;
        font-weight: bold;
        font-size: 10px;
      }
      .dbg-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1px 8px;
        font-size: 10px;
      }
      .dbg-grid b {
        color: #0f0;
      }
      .dbg-chain {
        max-height: 120px;
        overflow-y: auto;
        font-size: 9.5px;
      }
      .dbg-chain-item {
        display: flex;
        gap: 4px;
        flex-wrap: wrap;
        padding: 1px 0;
        border-bottom: 1px dotted #333;
      }
      .dbg-chain-tag {
        color: #00ddff;
        font-weight: bold;
        min-width: 65px;
      }
      .dbg-actions {
        margin-top: 6px;
        gap: 4px;
      }
      .dbg-btn-action {
        flex: 1;
      }
    `,
  ],
})
export class DebugOverlayComponent implements AfterViewInit, OnDestroy {
  enabled = false;
  minimized = false;
  position = { x: 8, y: 64 };

  // Datos en pantalla
  lastEvent = '—';
  coords = '—';
  targetTag = '—';
  targetIdClass = '—';
  touchAction = '—';
  pointerEvents = '—';
  zIndex = '—';
  position_css = '—';
  lastDefaultPrevented: boolean | string = '—';
  touchActionBad = false;
  pointerEventsBad = false;
  scrollTop = 0;
  scrollHeight = 0;
  clientHeight = 0;
  stackingChain: Array<{
    tag: string;
    pe: string;
    ta: string;
    z: string;
    pos: string;
  }> = [];
  counters = {
    touchstart: 0,
    touchmove: 0,
    touchend: 0,
    touchcancel: 0,
  };
  missingEnds = 0;

  private lastTouchstartAt = 0;
  private lastTarget: HTMLElement | null = null;
  private dragStart: { x: number; y: number; ox: number; oy: number } | null = null;
  private rafId = 0;
  private listeners: Array<{
    target: EventTarget;
    type: string;
    handler: any;
    opts: any;
  }> = [];
  private highlightedTimeout: any = null;

  constructor(private cdr: ChangeDetectorRef, private zone: NgZone) {
    // Detección de activación
    const params = new URLSearchParams(window.location.search);
    if (params.get('nodebug') === '1') {
      this.enabled = false;
      return;
    }
    if (
      params.get('debug') === '1' ||
      localStorage.getItem('debugOverlay') === '1'
    ) {
      this.enabled = true;
    }
    // Hook global para activarlo desde consola del Web Inspector
    (window as any).enableDebugOverlay = (on: boolean = true) => {
      this.enabled = on;
      if (on) {
        localStorage.setItem('debugOverlay', '1');
      } else {
        localStorage.removeItem('debugOverlay');
      }
      this.cdr.detectChanges();
    };
  }

  ngAfterViewInit(): void {
    if (!this.enabled) {
      return;
    }
    this.zone.runOutsideAngular(() => this.attachListeners());
    this.rafId = requestAnimationFrame(() => this.tick());
  }

  ngOnDestroy(): void {
    this.detachListeners();
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
  }

  toggleMinimize() {
    this.minimized = !this.minimized;
  }

  onDragStart(e: TouchEvent) {
    const t = e.touches[0];
    this.dragStart = {
      x: t.clientX,
      y: t.clientY,
      ox: this.position.x,
      oy: this.position.y,
    };
  }
  onDrag(e: TouchEvent) {
    if (!this.dragStart) return;
    const t = e.touches[0];
    this.position = {
      x: this.dragStart.ox + (t.clientX - this.dragStart.x),
      y: this.dragStart.oy + (t.clientY - this.dragStart.y),
    };
  }
  onDragEnd() {
    this.dragStart = null;
  }

  private attachListeners(): void {
    const add = (
      target: EventTarget,
      type: string,
      handler: any,
      opts: any = { capture: true, passive: true }
    ) => {
      target.addEventListener(type, handler, opts);
      this.listeners.push({ target, type, handler, opts });
    };

    add(document, 'touchstart', (e: TouchEvent) => {
      this.counters.touchstart++;
      this.lastTouchstartAt = performance.now();
      this.missingEnds++;
      this.captureEvent('touchstart', e);
    });
    add(document, 'touchmove', (e: TouchEvent) => {
      this.counters.touchmove++;
      this.captureEvent('touchmove', e);
    });
    add(document, 'touchend', (e: TouchEvent) => {
      this.counters.touchend++;
      this.missingEnds = Math.max(0, this.missingEnds - 1);
      this.captureEvent('touchend', e);
    });
    add(document, 'touchcancel', (e: TouchEvent) => {
      this.counters.touchcancel++;
      this.missingEnds = Math.max(0, this.missingEnds - 1);
      this.captureEvent('touchcancel', e);
    });
  }

  private detachListeners(): void {
    this.listeners.forEach(({ target, type, handler, opts }) => {
      target.removeEventListener(type, handler, opts);
    });
    this.listeners = [];
  }

  private captureEvent(name: string, e: TouchEvent): void {
    this.lastEvent = name;
    const t = e.touches[0] || e.changedTouches?.[0];
    if (t) {
      this.coords = `(${Math.round(t.clientX)}, ${Math.round(t.clientY)})`;
      const target = (t.target as HTMLElement) || (e.target as HTMLElement);
      this.lastTarget = target;
      this.captureTargetInfo(target);
    }
    this.lastDefaultPrevented = (e as any).defaultPrevented;
  }

  private captureTargetInfo(target: HTMLElement | null): void {
    if (!target) return;
    this.targetTag = target.tagName.toLowerCase();
    const idStr = target.id ? `#${target.id}` : '';
    const classStr =
      typeof target.className === 'string'
        ? '.' + target.className.split(/\s+/).filter(Boolean).slice(0, 3).join('.')
        : '';
    this.targetIdClass = (idStr + classStr).slice(0, 60) || '—';

    const cs = getComputedStyle(target);
    this.touchAction = cs.touchAction;
    this.pointerEvents = cs.pointerEvents;
    this.zIndex = cs.zIndex;
    this.position_css = cs.position;

    this.touchActionBad =
      cs.touchAction === 'none' ||
      cs.touchAction === 'pinch-zoom' ||
      cs.touchAction === 'manipulation';
    this.pointerEventsBad = cs.pointerEvents === 'none';

    // Cadena de stacking
    this.stackingChain = [];
    let el: HTMLElement | null = target;
    let depth = 0;
    while (el && el !== document.body && depth < 14) {
      const ecs = getComputedStyle(el);
      this.stackingChain.push({
        tag:
          el.tagName.toLowerCase() +
          (el.id ? '#' + el.id : '') +
          (typeof el.className === 'string' && el.className
            ? '.' + el.className.split(/\s+/)[0]
            : ''),
        pe: ecs.pointerEvents,
        ta: ecs.touchAction,
        z: ecs.zIndex,
        pos: ecs.position,
      });
      el = el.parentElement;
      depth++;
    }
  }

  private tick(): void {
    if (!this.enabled) {
      this.rafId = requestAnimationFrame(() => this.tick());
      return;
    }
    // Actualizar scroll info del ion-content activo
    const ionContent = document.querySelector(
      'ion-content'
    ) as HTMLElement | null;
    if (ionContent) {
      const scrollEl = (ionContent.shadowRoot?.querySelector(
        '.inner-scroll'
      ) as HTMLElement | null) || ionContent;
      this.scrollTop = Math.round(scrollEl.scrollTop);
      this.scrollHeight = scrollEl.scrollHeight;
      this.clientHeight = scrollEl.clientHeight;
    }
    this.cdr.detectChanges();
    this.rafId = requestAnimationFrame(() => this.tick());
  }

  dumpToConsole(): void {
    /* eslint-disable no-console */
    console.group('🔬 DebugOverlay snapshot');
    console.log('counters', this.counters);
    console.log('missingEnds', this.missingEnds);
    console.log('lastEvent', this.lastEvent);
    console.log('coords', this.coords);
    console.log('targetTag', this.targetTag);
    console.log('targetIdClass', this.targetIdClass);
    console.log('touchAction', this.touchAction);
    console.log('pointerEvents', this.pointerEvents);
    console.log('zIndex', this.zIndex);
    console.log('position_css', this.position_css);
    console.log('defaultPrevented', this.lastDefaultPrevented);
    console.log('scrollTop', this.scrollTop);
    console.log('scrollHeight', this.scrollHeight);
    console.log('clientHeight', this.clientHeight);
    console.table(this.stackingChain);
    console.groupEnd();
    /* eslint-enable no-console */
  }

  highlightTarget(): void {
    if (!this.lastTarget) return;
    const prev = this.lastTarget.style.outline;
    this.lastTarget.style.outline = '3px solid magenta';
    this.lastTarget.style.outlineOffset = '-3px';
    if (this.highlightedTimeout) clearTimeout(this.highlightedTimeout);
    this.highlightedTimeout = setTimeout(() => {
      if (this.lastTarget) {
        this.lastTarget.style.outline = prev;
        this.lastTarget.style.outlineOffset = '';
      }
    }, 3000);
  }
}
